// src/routes.js
// Define dashboard and authentication routes
//
// --- UPDATE (SLA AI + smart sidebar) ------------------------------------------
//   • NEW route: SLA Command Center → /admin/sla-ai (views/admin/SlaAiCommandCenter, from the
//     SLA AI package). Supervision only (admin, manager, supervisor) — matches the API guard.
//   • Every route now carries sidebar metadata (all optional, nothing else reads them):
//       section   which sidebar group it sits in (see ROUTE_SECTIONS)
//       keywords  extra words the sidebar search matches ("sla", "breach", "dispatch" …)
//       ai        shows the AI tag next to the name
//       badgeKey  live counter the sidebar shows: 'sla' (breached / at risk) or 'notify' (unread)
//       role      who sees it: role ids and/or groups ('@supervision', '@office', '@field' …)
//   • Push Notifications is now open to office roles (admin, manager, supervisor, staff): the
//     FlowSense Notify console enforces what each of them may send (staff: no critical / no
//     "everyone"). Put role: ['admin'] back if you want it admin-only.
//   • canAccessRoute(route, user) — used by the sidebar. Route guards in the admin layout should
//     use it too (hiding a link is not access control on its own).
//   • (ML Ops) 'AI Model Training' → 'AI Intelligence Center' (same path /admin/ai/grading), office roles.
// --- END UPDATE ---------------------------------------------------------------
// --- UPDATE (forgot / reset password) ------------------------------------------
//   • NEW auth routes: /auth/forgot-password (ForgotPassword) and /auth/reset-password
//     (ResetPassword), from the password-reset package. Same shape as Sign In: layout '/auth',
//     component reference, displayInSidebar: false. No `role`: they must open signed out.
// --- END UPDATE ---------------------------------------------------------------

import { Icon } from '@chakra-ui/react';
// Import specific icons from react-icons
import {
  MdHome,
  MdAssessment,
  MdBuild,
  MdCalendarToday,
  MdPeople,
  MdLock,
  MdPersonAdd,
  MdPublic,
  MdEmail,
  MdNotificationsActive,
  MdCrisisAlert,
  MdInventory,
  MdShoppingBasket,
  MdTravelExplore,
  MdSettings,
  MdBarChart,
  MdContentCopy,
  MdSupportAgent,
  MdPsychology,
  MdAddRoad, // <-- Added Icon for Road Works
  MdSpeed, // --- UPDATE: SLA Command Center
  MdEngineering, // --- UPDATE: Field Operations (was a duplicate MdPeople)
  MdCampaign, // --- UPDATE: Alert System (was a duplicate MdNotificationsActive)
} from 'react-icons/md';
import { hasRole } from './config/roles'; // --- UPDATE (from the FlowSense Notify + RBAC package)

// --- Page Components ---
// Admin Pages
import LiveReadingsPage from './views/admin/LiveReadingsPage';
import DashboardPage from './views/admin/DashboardPage';
import WorkOrderPage from './views/admin/WorkOrderPage';
import IssueManagementPage from './views/admin/IssueManagementPage';
import CalendarPage from './views/admin/CalendarPage';
import UserManagementPage from './views/admin/UserManagementPage';
import PublicUtilitiesPage from './views/admin/PublicUtilitiesPage';
import MailPage from './views/admin/MailPage';
import PushNotificationPage from "./views/admin/PushNotificationPage";
import AlertSystemPage from './views/admin/AlertSystemPage';
import InventoryManagementPage from "./views/admin/InventoryManagementPage";
import PurchaseOrderPage from "./views/admin/PurchaseOrderPage";
import DigitalTwinPage from './views/admin/DigitalTwinPage';
import SettingsPage from "./pages/SettingsPage";
import AnalyticsPage from './views/admin/AnalyticsPage';
import FieldOperationsPage from './views/admin/FieldOperationsPage';
import TemplateManager from './views/admin/notifications/TemplateManager';
import HelpdeskPage from './views/admin/HelpdeskPage';
import AIModelGradingPage from './views/admin/AIModelGradingPage';
import RoadWorksPage from './views/admin/RoadWorksPage'; // <-- Imported Road Works Page
import SlaAiCommandCenter from './views/admin/SlaAiCommandCenter'; // --- UPDATE: SLA AI


// Auth Pages
import SignInPage from './views/auth/SignIn';
import SignUpPage from './views/auth/SignUp';
import ForgotPasswordPage from './views/auth/ForgotPassword'; // --- UPDATE (forgot / reset password)
import ResetPasswordPage from './views/auth/ResetPassword'; // --- UPDATE (forgot / reset password)

// --- UPDATE: sidebar groups, in display order
export const ROUTE_SECTIONS = Object.freeze([
  { key: 'overview', label: 'Overview' },
  { key: 'operations', label: 'Operations' },
  { key: 'assets', label: 'Assets & Supply' },
  { key: 'comms', label: 'Communications' },
  { key: 'intelligence', label: 'Intelligence' },
  { key: 'admin', label: 'Administration' },
]);

/**
 * True when the user may open the route. A route without `role` is open to every signed-in user.
 * `role` accepts a string or an array of role ids and '@groups' (see config/roles.js).
 */
export const canAccessRoute = (route, user) => {
  if (!route) return false;
  const required = route.role == null ? [] : [].concat(route.role);
  if (!required.length) return true;
  if (!user) return false;
  return hasRole(user, required);
};
// --- END UPDATE

const routes = [
  // --- Admin Routes ---
  {
    name: 'Dashboard',
    layout: '/admin',
    path: '/dashboard',
    icon: <Icon as={MdHome} width='20px' height='20px' color='inherit' />,
    component: DashboardPage,
    displayInSidebar: true,
    section: 'overview',
    keywords: ['home', 'overview', 'kpi'],
  },
  {
    name: 'FlowSense Hub',
    layout: '/admin',
    path: '/digital-twin',
    icon: <Icon as={MdTravelExplore} width='20px' height='20px' color='inherit' />,
    component: DigitalTwinPage,
    displayInSidebar: true,
    section: 'overview',
    keywords: ['digital twin', 'map', 'network', 'gis'],
    ai: true,
  },
  {
    name: 'Analytics Hub',
    layout: '/admin',
    path: '/analytics',
    icon: <Icon as={MdBarChart} width='20px' height='20px' color='inherit' />,
    component: AnalyticsPage,
    displayInSidebar: true,
    section: 'overview',
    keywords: ['reports', 'charts', 'trends', 'insights'],
    ai: true,
  },
  {
    name: 'Live Readings',
    layout: '/admin',
    path: '/readings',
    icon: <Icon as={MdAssessment} width='20px' height='20px' color='inherit' />,
    component: LiveReadingsPage,
    displayInSidebar: true,
    section: 'overview',
    keywords: ['sensors', 'telemetry', 'water level', 'pressure', 'wells'],
  },
  // --- UPDATE: SLA AI ---------------------------------------------------------
  {
    name: 'SLA Command Center',
    layout: '/admin',
    path: '/sla-ai',
    icon: <Icon as={MdSpeed} width='20px' height='20px' color='inherit' />,
    component: SlaAiCommandCenter,
    displayInSidebar: true,
    section: 'operations',
    keywords: ['sla', 'deadline', 'breach', 'overdue', 'escalation', 'reassign', 'grade', 'compliance', 'at risk'],
    role: ['@supervision'],
    ai: true,
    badgeKey: 'sla',
  },
  // --- END UPDATE -------------------------------------------------------------
  {
    name: 'Work Orders',
    layout: '/admin',
    path: '/workorders',
    icon: <Icon as={MdBuild} width='20px' height='20px' color='inherit' />,
    component: WorkOrderPage,
    displayInSidebar: true,
    section: 'operations',
    keywords: ['jobs', 'tickets', 'dispatch', 'assign', 'wo'],
  },
  {
    name: 'Road Works', // <-- Added Road Works Route
    layout: '/admin',
    path: '/road-works',
    icon: <Icon as={MdAddRoad} width='20px' height='20px' color='inherit' />,
    component: RoadWorksPage,
    displayInSidebar: true,
    section: 'operations',
    keywords: ['roads', 'potholes', 'asphalt', 'inspection', 'paving', 'public reports', 'phone reports', 'road damage'], // --- UPDATE (Phone AI Helpdesk)
  },
  {
    name: 'Field Operations',
    layout: '/admin',
    path: '/field-operations',
    icon: <Icon as={MdEngineering} w='20px' h='20px' color='inherit' />,
    component: FieldOperationsPage,
    section: 'operations',
    keywords: ['crews', 'technicians', 'contractors', 'gps', 'tracking'],
  },
  {
    name: 'Issue Management',
    layout: '/admin',
    // Path kept as-is so existing links keep working (the sidebar decodes the %20 to highlight it)
    path: '/Issue Management',
    icon: <Icon as={MdCrisisAlert} width='20px' height='20px' color='inherit' />,
    component: IssueManagementPage,
    displayInSidebar: true,
    section: 'operations',
    keywords: ['issues', 'reports', 'complaints', 'leaks'],
  },
  {
    name: 'Helpdesk',
    layout: '/admin',
    path: '/helpdesk',
    icon: <Icon as={MdSupportAgent} width='20px' height='20px' color='inherit' />,
    component: HelpdeskPage,
    section: 'operations',
    keywords: ['support', 'customers', 'calls', 'tickets', 'phone', '24 hour', 'ai assistant', 'public reports', 'potholes', 'flooding', 'complaints', 'transcripts', 'hot spots'], // --- UPDATE (Phone AI Helpdesk)
    ai: true, // --- UPDATE (Phone AI Helpdesk): 24-hour phone AI command center
  },
  {
    name: 'Calendar',
    layout: '/admin',
    path: '/calendar',
    icon: <Icon as={MdCalendarToday} width='20px' height='20px' color='inherit' />,
    component: CalendarPage,
    displayInSidebar: true,
    section: 'operations',
    keywords: ['schedule', 'appointments', 'planner'],
  },
  {
    name: 'Public Utilities',
    layout: '/admin',
    path: '/utilities',
    icon: <Icon as={MdPublic} width='20px' height='20px' color='inherit' />,
    component: PublicUtilitiesPage,
    displayInSidebar: true,
    section: 'assets',
    keywords: ['assets', 'meters', 'hydrants', 'valves', 'pipes'],
  },
  {
    name: "Inventory",
    layout: "/admin",
    path: "/inventory",
    icon: <Icon as={MdInventory} width='20px' height='20px' color='inherit' />,
    component: InventoryManagementPage,
    displayInSidebar: true,
    section: 'assets',
    keywords: ['stock', 'parts', 'van stock', 'warehouse'],
  },
  {
    name: "Purchase Orders",
    layout: "/admin",
    path: "/purchase-orders",
    icon: <Icon as={MdShoppingBasket} width='20px' height='20px' color='inherit' />,
    component: PurchaseOrderPage,
    displayInSidebar: true,
    section: 'assets',
    keywords: ['po', 'procurement', 'suppliers', 'buy'],
  },
  {
    name: 'Send Mail',
    layout: '/admin',
    path: '/mail',
    icon: <Icon as={MdEmail} width='20px' height='20px' color='inherit' />,
    component: MailPage,
    displayInSidebar: true,
    section: 'comms',
    keywords: ['email', 'escalation mail', 'outbox'],
  },
  {
    name: "Push Notifications",
    layout: "/admin",
    path: "/push-notifications",
    icon: <Icon as={MdNotificationsActive} width='20px' height='20px' color='inherit' />,
    component: PushNotificationPage,
    role: ['@office'], // --- UPDATE: was ["admin"] — the Notify console enforces per-role limits
    displayInSidebar: true,
    section: 'comms',
    keywords: ['notify', 'broadcast', 'announcement', 'alerts', 'push'],
    badgeKey: 'notify',
  },
  {
    name: "Alert System",
    layout: "/admin",
    path: "/alert-system",
    icon: <Icon as={MdCampaign} width='20px' height='20px' color='inherit' />,
    component: AlertSystemPage,
    role: ["admin"],
    displayInSidebar: true,
    section: 'comms',
    keywords: ['public alerts', 'outage', 'citizens'],
  },
  {
    name: "Template Manager",
    layout: "/admin",
    path: "/notifications/templates",
    icon: <Icon as={MdContentCopy} width='20px' height='20px' color='inherit' />,
    component: TemplateManager,
    section: 'comms',
    keywords: ['templates', 'messages'],
  },
  // --- UPDATE (ML Ops): the AI Model Training page is now the AI Intelligence Center —
  //     every model and AI engine, registry, training & autopilot, human review, health &
  //     drift, and Ask FlowSense AI. Office roles can open it (the API enforces who may
  //     train / promote / delete); field roles use Ask FlowSense AI from the widgets.
  {
    path: '/ai/grading',
    name: 'AI Intelligence Center',
    icon: <Icon as={MdPsychology} width='20px' height='20px' color='inherit' />,
    component: AIModelGradingPage,
    layout: '/admin',
    section: 'intelligence',
    keywords: ['ai', 'ml', 'machine learning', 'model', 'models', 'training', 'retrain', 'autopilot', 'registry', 'promote', 'rollback', 'drift', 'rlhf', 'human review', 'grading', 'explain', 'ask ai', 'dispatcher', 'classifier', 'parts predictor', 'rul'],
    ai: true,
    role: '@office',
  },
  // --- END UPDATE
  {
    name: 'User Management',
    layout: '/admin',
    path: '/users',
    icon: <Icon as={MdPeople} width='20px' height='20px' color='inherit' />,
    component: UserManagementPage,
    displayInSidebar: true,
    section: 'admin',
    keywords: ['users', 'roles', 'staff', 'inspectors', 'contractors', 'permissions'],
  },
  {
    name: "Settings",
    layout: "/admin",
    path: "/settings",
    icon: <Icon as={MdSettings} width='20px' height='20px' color='inherit' />,
    component: SettingsPage,
    displayInSidebar: true,
    section: 'admin',
    keywords: ['preferences', 'configuration', 'profile'],
  },

  // --- Auth Routes ---
  {
    name: 'Sign In',
    layout: '/auth',
    path: '/sign-in',
    icon: <Icon as={MdLock} width='20px' height='20px' color='inherit' />,
    component: SignInPage,
    displayInSidebar: false,
  },
  {
    name: 'Sign Up',
    layout: '/auth',
    path: '/sign-up',
    icon: <Icon as={MdPersonAdd} width='20px' height='20px' color='inherit' />,
    component: SignUpPage,
    displayInSidebar: false,
  },
  // --- UPDATE (forgot / reset password) ---------------------------------------
  {
    name: 'Forgot Password',
    layout: '/auth',
    path: '/forgot-password',
    icon: <Icon as={MdLock} width='20px' height='20px' color='inherit' />,
    component: ForgotPasswordPage,
    displayInSidebar: false,
  },
  {
    name: 'Reset Password',
    layout: '/auth',
    path: '/reset-password',
    icon: <Icon as={MdLock} width='20px' height='20px' color='inherit' />,
    component: ResetPasswordPage,
    displayInSidebar: false,
  },
  // --- END UPDATE -------------------------------------------------------------
];

export default routes;
